<script lang="ts" setup>
import { ElMessage } from "element-plus";
import { ref, onUnmounted, computed, watch, reactive } from "vue";
import router from "../../router";
import axios from "axios";
import { DateTime } from "luxon";
import { filterCustomerDeliveryLogs, customerActivityLabel } from "../../utils/customerDeliveryLogs";

declare const google: any;
const loading = ref(true);
const orderObject = reactive({ data: <any>null });
const myMapRef = ref<any>(null);
const mapCenter = { lat: -1.23266, lng: 36.77494 };
const routePath = ref<{ lat: number; lng: number }[]>([]);

const isValidTripData = (d: any): boolean => {
  if (!d || typeof d !== "object") return false;
  const od = d.order_details;
  if (!od || typeof od !== "object") return false;
  if (od.order_number == null || od.order_status_text == null || od.order_status == null) return false;
  if (!Array.isArray(d.paths)) return false;
  if (!Array.isArray(d.delivery_logs)) return false;
  return true;
};

const orderDetailsLoaded = computed(() => orderObject.data !== null);

const droppedDeliveryLog = computed(() => {
  if (!orderObject.data) return null;
  return orderObject.data.delivery_logs.find((log: any) => log.log_type === "Dropped") ?? null;
});

const customerDeliveryLogs = computed(() =>
  filterCustomerDeliveryLogs(orderObject.data?.delivery_logs),
);

// Live tracking only makes sense while the order is in motion. Once the order
// status passes the delivered threshold (>= 5 in this codebase), switch to a
// "delivery complete" state instead of showing the map and live timeline.
const isComplete = computed(() => {
  const s = orderObject.data?.order_details?.order_status;
  return typeof s === "number" && s >= 5;
});

// Stop completion state — derived from order_status:
//   >= 3 (picked)    → pickup stop is done
//   >= 4 (delivered) → all stops are done
const isStopComplete = (index: number): boolean => {
  const s = orderObject.data?.order_details?.order_status;
  if (typeof s !== "number") return false;
  if (s >= 4) return true;
  if (s >= 3 && index === 0) return true;
  return false;
};

// `isPoll` is true for the 30s background refresh — a transient failure then
// must keep the last good data on screen and stay quiet (no error toast).
const getTripDetails = async (trackingToken: string, isPoll = false) => {
  const fail = (message: string) => {
    if (isPoll) { console.log("trip refresh failed:", message); return; }
    orderObject.data = null;
    ElMessage({ message, type: "error" });
  };
  try {
    const base = import.meta.env.VITE_ORDERS_SERVICE;
    // Build an isolated axios call so we never send the logged-in user's
    // Bearer token (if any) from axios.defaults to this public endpoint.
    const response = await axios.get(`${base}trip?token=${encodeURIComponent(trackingToken)}`, {
      transformRequest: [(data, headers) => {
        if (headers) delete (headers as any).Authorization;
        return data;
      }],
    });
    if (response.status == 200 && response.data.status) {
      const d = response.data.data;
      if (isValidTripData(d)) {
        orderObject.data = d;
      } else {
        fail(response.data.message || "Unable to load trip.");
      }
    } else {
      fail(response.data.message || "Order not found");
    }
  } catch (er) {
    console.log(er);
    fail("Something went wrong");
  } finally {
    loading.value = false;
  }
};

const fetchDirectionsRoute = async (paths: any[]) => {
  if (paths.length < 2) { routePath.value = []; return; }
  try {
    const directionsService = new google.maps.DirectionsService();
    const waypoints = paths.slice(1, -1).map((p: any) => ({
      location: { lat: p.lat, lng: p.lng },
      stopover: true,
    }));
    const result = await directionsService.route({
      origin: { lat: paths[0].lat, lng: paths[0].lng },
      destination: { lat: paths[paths.length - 1].lat, lng: paths[paths.length - 1].lng },
      waypoints,
      travelMode: google.maps.TravelMode.DRIVING,
      optimizeWaypoints: false,
    });
    const path: { lat: number; lng: number }[] = [];
    result.routes[0].legs.forEach((leg: any) => {
      leg.steps.forEach((step: any) => {
        step.path.forEach((pt: any) => {
          path.push({ lat: pt.lat(), lng: pt.lng() });
        });
      });
    });
    routePath.value = path;
  } catch (e) {
    routePath.value = paths.map((p: any) => ({ lat: p.lat, lng: p.lng }));
  }
};

const isMobileViewport = () => typeof window !== "undefined" && window.innerWidth <= 640;

const updateBounds = () => {
  const mapInstance = myMapRef.value;
  try {
    if (mapInstance && orderObject.data) {
      const paths = orderObject.data.paths;
      if (!paths?.length) return;
      mapInstance.$mapPromise.then((map: any) => {
        const bounds = new google.maps.LatLngBounds();
        paths.forEach((p: any) => bounds.extend(p));
        // Mobile bottom-sheet sits at the bottom of the viewport (~55vh).
        // Desktop floating panel sits on the left (~430px wide incl. margin).
        const padding = isMobileViewport()
          ? { left: 24, top: 80, right: 24, bottom: Math.round(window.innerHeight * 0.55) + 24 }
          : { left: 460, top: 40, right: 40, bottom: 40 };
        map.fitBounds(bounds, padding);
      });
    }
  } catch (er) {
    console.log(er);
  }
};

watch(myMapRef, (googleMap) => {
  if (googleMap) {
    googleMap.$mapPromise.then(() => updateBounds());
  }
});

watch(() => orderObject.data, (val) => {
  if (val?.paths?.length) {
    fetchDirectionsRoute(val.paths);
    updateBounds();
  } else {
    routePath.value = [];
  }
});

// --- Auto-refresh: re-fetch trip data every 30s while the order is live ---
const POLL_MS = 30000;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let activeToken: string | null = null;

const isTerminalStatus = (): boolean => {
  // Terminal only when the composed status is one that can never
  // transition again — cancelled, docs-approved-delivered, or
  // ops-closed. Any other order_status=4 (docs pending / rejected /
  // uploaded-not-yet-reviewed) MUST keep polling: the customer-visible
  // label flips as ops works through the review queue and stopping
  // early leaves the page frozen on "Pending approval" forever.
  const od = orderObject.data?.order_details;
  if (!od) return false;
  const view = orderStatusView({
    status: od.status,
    order_status: od.order_status,
    verification_status: od.verification_status,
    order_status_text: od.order_status_text,
  });
  return view.kind === "cancelled" || view.kind === "delivered" || view.kind === "closed";
};

const stopPolling = () => {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
};

const startPolling = () => {
  if (pollTimer || !activeToken) return;
  pollTimer = setInterval(() => {
    if (isTerminalStatus()) { stopPolling(); return; }
    if (activeToken) getTripDetails(activeToken, true);
  }, POLL_MS);
};

onUnmounted(stopPolling);

const tokenQuery = router.currentRoute.value.query.token;
if (tokenQuery != null && String(tokenQuery).trim() !== "") {
  activeToken = String(tokenQuery);
  getTripDetails(activeToken);
  startPolling();
} else {
  loading.value = false;
  ElMessage({ message: "Tracking token is required", type: "error" });
}

// Render backend timestamps (ISO or SQL "yyyy-MM-dd HH:mm:ss") as a
// human-readable string, e.g. "21 May 2026, 10:38".
const transformDate = (date: string) => {
  if (!date) return "";
  let dt = DateTime.fromISO(date);
  if (!dt.isValid) dt = DateTime.fromSQL(date);
  return dt.isValid ? dt.toFormat("dd MMM yyyy, HH:mm") : String(date);
};

import { orderStatusView } from '../../utils/orderStatus';

// Composed status label + tone. Public /trip page reads verification_status
// from the same payload (order_details on the trip response was extended
// earlier in this feature ship). Recipients see the same "Pending
// approval" -> "Delivered" transition the customer does.
const statusView = computed(() => orderStatusView({
  status:              orderObject.data?.status,
  order_status:        orderObject.data?.order_details?.order_status,
  verification_status: orderObject.data?.order_details?.verification_status,
  order_status_text:   orderObject.data?.order_details?.order_status_text,
}));

const statusColor = computed(() => {
  switch (statusView.value.tone) {
    case 'pending':   return { bg: 'rgba(220,38,38,0.10)',  text: '#dc2626', dot: '#dc2626' };
    case 'active':    return { bg: 'rgba(37,99,235,0.10)',  text: '#2563eb', dot: '#2563eb' };
    case 'warning':   return { bg: 'rgba(245,158,11,0.15)', text: '#d97706', dot: '#f59e0b' };
    case 'danger':    return { bg: 'rgba(180,83,9,0.12)',   text: '#b45309', dot: '#f59e0b' };
    case 'delivered': return { bg: 'rgba(34,197,94,0.12)',  text: '#16a34a', dot: '#22c55e' };
    case 'cancelled': return { bg: 'rgba(109,40,217,0.10)', text: '#6d28d9', dot: '#8b5cf6' };
    default:          return { bg: 'rgba(156,163,175,0.15)', text: '#6b7280', dot: '#9ca3af' };
  }
});

const MARKER_COLORS = ['#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const getMarkerIcon = (index: number) => {
  const color = MARKER_COLORS[index % MARKER_COLORS.length];
  const label = String.fromCharCode(65 + index);
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <filter id="shd${index}" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.25)"/>
      </filter>
      <ellipse cx="18" cy="42" rx="6" ry="2.5" fill="rgba(0,0,0,0.12)"/>
      <path d="M18 2C10.268 2 4 8.268 4 16c0 10 14 26 14 26s14-16 14-26C32 8.268 25.732 2 18 2z" fill="${color}" filter="url(#shd${index})"/>
      <circle cx="18" cy="16" r="8" fill="white" opacity="0.92"/>
      <text x="18" y="20" text-anchor="middle" font-size="9" font-weight="700" font-family="DM Sans, sans-serif" fill="${color}">${label}</text>
    </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: 36, height: 44 },
    anchor: { x: 18, y: 44 },
  };
};

const polylineOptions = {
  strokeColor: "#2563eb",
  strokeOpacity: 1.0,
  strokeWeight: 5,
  geodesic: true,
};

const mapOptions = {
  styles: [
    { elementType: "geometry", stylers: [{ color: "#eaf0fb" }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { elementType: "labels.text.fill", stylers: [{ color: "#3d4a5c" }] },
    { elementType: "labels.text.stroke", stylers: [{ color: "#ffffff" }] },
    { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
    { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#c8e6c9" }] },
    { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#388e3c" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#dce6f5" }] },
    { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#b8cef5" }] },
    { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#9ab8e8" }] },
    { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#12284A" }] },
    { featureType: "road.local", stylers: [{ visibility: "simplified" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: "#a8cce0" }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3d6b8a" }] },
  ],
  disableDefaultUI: true,
  zoomControl: false,
};
</script>

<template>
  <div style="padding:0; position:relative; height:100vh; width:100vw;" v-loading="loading">

    <!-- Completed state — shown once delivery is finished -->
    <div v-if="orderDetailsLoaded && isComplete" class="trip-complete">
      <div class="trip-complete-card">
        <div class="trip-complete-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="9 12 11 14 15 10"></polyline>
          </svg>
        </div>
        <div class="trip-complete-title">Delivery complete</div>
        <div class="trip-complete-order">Order {{ orderObject.data.order_details.order_number }}</div>
        <div class="trip-complete-sub" v-if="droppedDeliveryLog">
          Delivered on {{ transformDate(droppedDeliveryLog.log_time) }}
        </div>
        <div class="trip-complete-sub" v-else>
          This delivery has been completed.
        </div>
      </div>
    </div>

    <div class="trip-outer-panel" v-else>
      <div class="trip-panel" v-if="orderDetailsLoaded">

        <div class="trip-panel-head">
          <div class="trip-brand">Xobo</div>

          <div class="trip-order-title-row">
            <div class="trip-order-number">{{ orderObject.data.order_details.order_number }}</div>
            <div
              class="trip-status-badge"
              :style="{ background: statusColor.bg, color: statusColor.text }"
              :title="statusView.hint"
            >
              <span class="trip-status-dot" :style="{ background: statusColor.dot }"></span>
              {{ statusView.label }}
            </div>
          </div>

          <div class="trip-stops">
            <div
              v-for="(path, index) in orderObject.data.paths"
              :key="index"
              class="trip-stop-row"
            >
              <div class="trip-stop-icon-wrap">
                <div
                  v-if="isStopComplete(Number(index))"
                  class="trip-stop-dot trip-stop-dot--done"
                  :aria-label="`Stop ${Number(index) + 1} completed`"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="4" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div
                  v-else
                  class="trip-stop-dot"
                  :class="Number(index) === 0 ? 'trip-stop-dot--pickup' : 'trip-stop-dot--drop'"
                ></div>
                <div class="trip-stop-line" v-if="Number(index) < orderObject.data.paths.length - 1"></div>
              </div>
              <div class="trip-stop-label">
                <div class="trip-stop-type">
                  {{ Number(index) === 0 ? 'Pick-up' : `Drop-off${orderObject.data.paths.length > 2 ? ' ' + index : ''}` }}
                </div>
                <div class="trip-stop-name">{{ path.name }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="trip-panel-body">

          <div class="trip-section">
            <div class="trip-section-title">Trip</div>
            <div class="trip-info-grid">
              <div class="trip-info-row">
                <span class="trip-info-label">Scheduled</span>
                <span class="trip-info-value">{{ transformDate(orderObject.data.order_details.scheduled_time) }}</span>
              </div>
              <div class="trip-info-row" v-if="orderObject.data.order_details.order_status > 3 && droppedDeliveryLog">
                <span class="trip-info-label">Delivered</span>
                <span class="trip-info-value">
                  {{ transformDate(droppedDeliveryLog.log_time) }}
                </span>
              </div>
            </div>
          </div>

          <div class="trip-section" v-if="orderObject.data.order_details.order_status != 1 && orderObject.data.driver_details && orderObject.data.driver_details.name">
            <div class="trip-section-title">Driver</div>
            <div class="trip-driver-card">
              <div class="trip-driver-avatar">{{ orderObject.data.driver_details.name?.charAt(0)?.toUpperCase() }}</div>
              <div class="trip-driver-info">
                <div class="trip-driver-name">{{ orderObject.data.driver_details.name }}</div>
                <div class="trip-driver-meta">{{ orderObject.data.driver_details.phone_number }}</div>
                <div class="trip-driver-meta" v-if="orderObject.data.driver_details.vehicle_type_name">{{ orderObject.data.driver_details.vehicle_type_name }}</div>
              </div>
            </div>
          </div>

          <div class="trip-section">
            <div class="trip-section-title">Activity</div>
            <div class="trip-timeline">
              <div
                v-for="(activity, index) in customerDeliveryLogs"
                :key="index"
                class="trip-timeline-item"
              >
                <div class="trip-timeline-left">
                  <div class="trip-timeline-dot" :class="Number(index) === 0 ? 'trip-timeline-dot--active' : ''"></div>
                  <div class="trip-timeline-connector" v-if="Number(index) < customerDeliveryLogs.length - 1"></div>
                </div>
                <div class="trip-timeline-content">
                  <div class="trip-timeline-event">{{ customerActivityLabel(activity) }}</div>
                  <div class="trip-timeline-time">{{ transformDate(activity.log_time ?? '') }}</div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <GMapMap
      v-if="!isComplete"
      :center="mapCenter"
      :zoom="12"
      map-type-id="roadmap"
      :options="mapOptions"
      style="position:absolute; top:0; left:0; right:0; bottom:0; width:100%; height:100%;"
      ref="myMapRef"
    >
      <GMapMarker
        v-if="orderDetailsLoaded"
        :key="index"
        v-for="(m, index) in orderObject.data.paths"
        :position="m"
        :clickable="false"
        :draggable="false"
        :icon="getMarkerIcon(Number(index))"
      />
      <GMapPolyline
        v-if="routePath.length > 1"
        :path="routePath"
        :options="polylineOptions"
      />
    </GMapMap>

  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

.trip-outer-panel {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  margin: 0 auto;
  padding: 0 30px;
  pointer-events: none;
  z-index: 10;
}

.trip-panel {
  position: absolute;
  top: 24px;
  left: 30px;
  width: 400px;
  max-height: calc(100vh - 48px);
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(18,40,74,0.16), 0 2px 8px rgba(18,40,74,0.08);
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  pointer-events: all;
  font-family: 'DM Sans', sans-serif;
}
.trip-panel::-webkit-scrollbar { display: none; }

.trip-panel-head {
  background: linear-gradient(135deg, #12284A 0%, #1e3f73 100%);
  padding: 20px 20px 18px;
  border-radius: 16px 16px 0 0;
}

.trip-brand {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: rgba(255,255,255,0.6);
  text-transform: uppercase;
  margin-bottom: 14px;
}

.trip-order-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.trip-order-number {
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.02em;
}

.trip-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}
.trip-status-dot {
  width: 6px; height: 6px; border-radius: 50%;
}

.trip-stops { display: flex; flex-direction: column; gap: 4px; }
.trip-stop-row { display: flex; gap: 10px; align-items: flex-start; }
.trip-stop-icon-wrap { display: flex; flex-direction: column; align-items: center; padding-top: 4px; }
.trip-stop-dot {
  width: 10px; height: 10px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.7);
}
.trip-stop-dot--pickup { background: #22c55e; }
.trip-stop-dot--drop { background: #ef4444; }
.trip-stop-dot--done {
  width: 16px;
  height: 16px;
  background: #16a34a;
  border-color: #16a34a;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: -3px 0;
}
.trip-stop-line { width: 2px; flex: 1; min-height: 16px; background: rgba(255,255,255,0.25); margin-top: 2px; }
.trip-stop-label { color: #ffffff; padding-bottom: 10px; flex: 1; }
.trip-stop-type { font-size: 0.7rem; color: rgba(255,255,255,0.55); text-transform: uppercase; letter-spacing: 0.06em; }
.trip-stop-name { font-size: 0.875rem; font-weight: 500; }

.trip-panel-body { padding: 18px 20px; display: flex; flex-direction: column; gap: 22px; }
.trip-section-title { font-size: 0.72rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 10px; }
.trip-info-grid { display: flex; flex-direction: column; gap: 8px; }
.trip-info-row { display: flex; justify-content: space-between; font-size: 0.875rem; }
.trip-info-label { color: #6b7280; }
.trip-info-value { color: #12284A; font-weight: 500; }

.trip-driver-card {
  display: flex; gap: 12px; align-items: center;
  padding: 12px; border-radius: 12px;
  background: rgba(18,40,74,0.04);
}
.trip-driver-avatar {
  width: 44px; height: 44px; border-radius: 50%;
  background: linear-gradient(135deg, #12284A, #1e3f73);
  color: #ffffff; display: flex; align-items: center; justify-content: center;
  font-weight: 700;
}
.trip-driver-name { font-size: 0.95rem; font-weight: 600; color: #12284A; }
.trip-driver-meta { font-size: 0.8rem; color: #6b7280; }

.trip-timeline { display: flex; flex-direction: column; }
.trip-timeline-item { display: flex; gap: 12px; }
.trip-timeline-left { display: flex; flex-direction: column; align-items: center; }
.trip-timeline-dot { width: 10px; height: 10px; border-radius: 50%; background: #d1d5db; margin-top: 4px; }
.trip-timeline-dot--active { background: #2563eb; box-shadow: 0 0 0 4px rgba(37,99,235,0.2); }
.trip-timeline-connector { width: 2px; flex: 1; min-height: 22px; background: #e5e7eb; margin-top: 4px; }
.trip-timeline-content { padding-bottom: 14px; flex: 1; }
.trip-timeline-event { font-size: 0.875rem; color: #12284A; font-weight: 500; }
.trip-timeline-time { font-size: 0.75rem; color: #9ca3af; margin-top: 2px; }

/* ── Completed state ──────────────────────────── */
.trip-complete {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
  padding: 24px;
  font-family: 'DM Sans', sans-serif;
}
.trip-complete-card {
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 8px 40px rgba(18,40,74,0.10);
  padding: 36px 32px;
  max-width: 420px;
  width: 100%;
  text-align: center;
}
.trip-complete-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 14px;
}
.trip-complete-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #16a34a;
  margin-bottom: 8px;
}
.trip-complete-order {
  font-size: 1.05rem;
  font-weight: 600;
  color: #12284A;
  letter-spacing: 0.02em;
  margin-bottom: 6px;
}
.trip-complete-sub {
  font-size: 0.875rem;
  color: #6b7280;
}

/* ── Mobile bottom-sheet layout ─────────────────────────── */
@media (max-width: 640px) {
  .trip-outer-panel {
    padding: 0;
    inset: 0;
  }
  .trip-panel {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-height: 55vh;
    border-radius: 0;
    box-shadow: 0 -8px 32px rgba(18,40,74,0.18);
    /* Use sticky-style overscroll behavior so swipes don't propagate to the map */
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    padding-bottom: env(safe-area-inset-bottom, 0);
  }
  .trip-panel-head {
    border-radius: 0;
    padding: 16px 18px 14px;
  }
  .trip-brand {
    font-size: 0.7rem;
    margin-bottom: 10px;
  }
  .trip-order-title-row {
    margin-bottom: 14px;
  }
  .trip-order-number {
    font-size: 1.1rem;
  }
  .trip-stop-name {
    font-size: 0.825rem;
  }
  .trip-panel-body {
    padding: 16px 18px 24px;
    gap: 18px;
  }
  .trip-section-title {
    font-size: 0.7rem;
  }
  .trip-driver-avatar {
    width: 40px;
    height: 40px;
  }
  /* Completed-state card: tighten padding on tiny screens */
  .trip-complete {
    padding: 16px;
  }
  .trip-complete-card {
    padding: 28px 22px;
  }
  .trip-complete-title {
    font-size: 1.25rem;
  }
}
</style>
